import React from 'react';

import {Button, Icon, Text} from '@albert-io/atomic';
import {EmptyState, EmptyIllustration} from 'client/TeacherDashboard/components/EmptyState';
import {history} from 'client/history';

import {ADAPTIVE_SUBJECTS_PATH, STATUS_SEGMENTS} from './constants';
import type {SkillPathStatusSegment} from './types';
import type {EmptyStateSubject} from './useSkillPathsEmptyStateSubjects';
import {useSkillPathsEmptyStateSubjects} from './useSkillPathsEmptyStateSubjects';

const SKILL_PATH_ILLUSTRATION = (
  <EmptyIllustration
    badgeClassName='background-special-subtle content-special'
    icon='diagram-project'
  />
);

const SkillPathSubjectLinks = ({subjects}: {subjects: EmptyStateSubject[]}) => (
  <div className='gap-x1 flex flex-wrap items-center justify-center'>
    {subjects.map((subject) => (
      <Button
        key={subject.id}
        className={`!background-domain-${subject.curriculumAreaSlug} content-primary-inverse-static! gap-x0-half inline-flex items-center`}
        roundness='pill'
        variant='solid'
        onClick={() => history.pushState(null, `/adaptive-subjects/${subject.urlSlug}`)}
      >
        {subject.name}
        <Icon
          color='primary-inverse-static'
          icon='arrow-up-right'
          iconStyle='regular'
          size='inherit'
        />
      </Button>
    ))}
    <Button
      color='secondary'
      roundness='pill'
      variant='outlined'
      onClick={() => history.pushState(null, ADAPTIVE_SUBJECTS_PATH)}
    >
      View All Adaptive
    </Button>
  </div>
);

/** Whole scope has no skill paths. Points the teacher to adaptive subjects to start a path. */
export const SkillPathsOnboardingEmptyState = ({classroomId}: {classroomId?: string}) => {
  const {subjects} = useSkillPathsEmptyStateSubjects(classroomId);
  return (
    <EmptyState
      action={<SkillPathSubjectLinks subjects={subjects} />}
      illustration={SKILL_PATH_ILLUSTRATION}
    >
      <div className='gap-x1 flex flex-col'>
        <Text as='div' bold size='l'>
          No Skill Paths
        </Text>
        <Text as='div' className='max-w-[440px]' color='secondary'>
          Send skills from an adaptive subject to build a skill path for your students.
        </Text>
      </div>
    </EmptyState>
  );
};

/** The current status tab has no skill paths, but another status does. */
export const SkillPathStatusEmptyState = ({
  segment,
  classroomId
}: {
  segment: SkillPathStatusSegment;
  classroomId?: string;
}) => {
  const label = STATUS_SEGMENTS.find((s) => s.value === segment)?.label ?? '';
  const {subjects} = useSkillPathsEmptyStateSubjects(classroomId);
  return (
    <EmptyState
      action={<SkillPathSubjectLinks subjects={subjects} />}
      illustration={SKILL_PATH_ILLUSTRATION}
    >
      <Text as='div' size='l'>
        No {label} Skill Paths
      </Text>
    </EmptyState>
  );
};
